/**
 * TIKTOK ACCOUNT — /tiktok/account
 *
 * Not connected → one centred connect card. Connected → one header card per
 * advertiser (the default one is "IN USE"; the others offer "Use this
 * advertiser"), a TikTok identities card (the accounts ads can run under —
 * TikTok has no API to link one, so this only lists + explains + re-checks),
 * and a compact Connection card with what the OAuth grant gave us.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Check, ExternalLink, KeyRound, Music2, RefreshCw, ShieldAlert, Trash2, Unplug, UserCircle2, Wallet, Zap, BarChart2, Rocket,
} from 'lucide-react';
import { TiktokShell, TiktokNotice } from '@/components/tiktok/TiktokShell';
import { errMsg } from '@/components/tiktok/workspace/workspaceShared';
import { Button } from '@/components/Button';
import { Card, SectionHeader } from '@/components/Card';
import { StatusPill } from '@/components/StatusPill';
import { Spinner } from '@/components/google-ads/youtube/youtubeShared';
import { tiktokService, type TiktokAdAccount, type TiktokOauthStatus } from '@/services/tiktok.service';
import { refreshTiktokAccountCurrency } from '@/hooks/useTiktokAccountCurrency';

type AccountBalance = Awaited<ReturnType<typeof tiktokService.getAccountBalance>>;
type Identity = Awaited<ReturnType<typeof tiktokService.listIdentities>>[number];

const fmtMoney = (n?: number | null, cur?: string | null) =>
  n == null ? '—' : `${cur ? `${cur} ` : ''}${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

const BrandTile = ({ size = 'md' }: { size?: 'md' | 'lg' }) => (
  <span className={clsx(
    'flex shrink-0 items-center justify-center rounded-2xl bg-black text-white dark:bg-white dark:text-black',
    size === 'lg' ? 'h-16 w-16' : 'h-12 w-12',
  )}>
    <Music2 size={size === 'lg' ? 30 : 22} />
  </span>
);

const IconButton = ({ icon, title, onClick, disabled, danger }: {
  icon: React.ReactNode; title: string; onClick: () => void; disabled?: boolean; danger?: boolean;
}) => (
  <button type="button" onClick={onClick} disabled={disabled} title={title} aria-label={title}
    className={clsx(
      'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted transition-colors disabled:cursor-not-allowed disabled:opacity-40',
      danger ? 'hover:bg-danger-soft hover:text-danger' : 'hover:bg-surface-alt hover:text-text-primary',
    )}>
    {icon}
  </button>
);

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-4 py-2.5 text-sm">
    <span className="shrink-0 text-text-muted">{label}</span>
    <span className="min-w-0 text-right font-medium text-text-primary">{children}</span>
  </div>
);

// ── connect gate ─────────────────────────────────────────────────────────

function ConnectCard({ onConnect, busy, canConnect }: { onConnect: () => void; busy: boolean; canConnect: boolean }) {
  return (
    <div className="mx-auto max-w-lg pt-8">
      <Card className="p-8 text-center">
        <div className="flex justify-center"><BrandTile size="lg" /></div>
        <h2 className="mt-5 font-display text-xl font-semibold tracking-tight text-text-primary">Connect TikTok for Business</h2>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-text-muted">
          Authorise SlightorCRM with your TikTok Business advertiser. We pull your campaigns and performance in,
          and let you build and launch new ones from here.
        </p>
        {canConnect ? (
          <Button className="mt-6" onClick={onConnect} loading={busy} iconLeft={<Music2 size={14} />}>Connect TikTok</Button>
        ) : (
          <p className="mt-6 rounded-xl border border-warning/30 bg-warning-soft px-4 py-2.5 text-xs text-warning">
            TikTok OAuth isn't configured on this server yet — ask your administrator to add the app credentials.
          </p>
        )}
        <ul className="mt-8 grid gap-3 text-left sm:grid-cols-3">
          {[
            { Icon: Rocket, text: 'Launch campaigns, ad groups and ads in one workspace' },
            { Icon: BarChart2, text: 'Daily spend, impressions and clicks synced per campaign' },
            { Icon: Zap, text: 'Manage advertiser connections and registered app campaigns' },
          ].map(({ Icon, text }) => (
            <li key={text} className="flex items-start gap-2.5 text-xs text-text-secondary">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary"><Icon size={12} /></span>
              <span className="leading-relaxed">{text}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────

export default function TiktokAccount() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState<TiktokAdAccount[] | null>(null);
  const [oauthStatus, setOauthStatus] = useState<TiktokOauthStatus | null>(null);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [balances, setBalances] = useState<Record<string, AccountBalance | null>>({});
  const [identities, setIdentities] = useState<Identity[] | null>(null);
  const [identitiesLoading, setIdentitiesLoading] = useState(false);
  // Persist the "0 advertisers" warning across the URL-cleanup re-render so
  // the user keeps seeing the diagnostic banner after the toast disappears.
  const [zeroAdvertisersWarning, setZeroAdvertisersWarning] = useState(false);

  // OAuth callback outcome arrives in the URL.
  useEffect(() => {
    if (searchParams.get('connected') === '1') {
      const added = Number(searchParams.get('advertisers') ?? 0);
      const reconnected = Number(searchParams.get('reconnected') ?? 0);
      const skipped = Number(searchParams.get('skipped') ?? 0);
      if (added + reconnected > 0) {
        toast.success(`TikTok connected — ${added} added, ${reconnected} reconnected`);
        setZeroAdvertisersWarning(false);
      } else if (!skipped) {
        toast.error('TikTok returned no advertisers. Review access and reconnect.');
        setZeroAdvertisersWarning(true);
      }
      if (skipped > 0) toast(`${skipped} advertiser${skipped === 1 ? '' : 's'} could not be added because the account allowance is full. Existing connections were preserved.`, { duration: 8000 });
      const next = new URLSearchParams(searchParams);
      next.delete('connected'); next.delete('advertisers'); next.delete('reconnected'); next.delete('skipped');
      setSearchParams(next, { replace: true });
    }
    const err = searchParams.get('error');
    if (err) {
      toast.error(`TikTok OAuth failed: ${err}`);
      const next = new URLSearchParams(searchParams);
      next.delete('error');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(() =>
    tiktokService.listAccounts()
      .then((rows) => {
        setLoadError('');
        setAccounts(rows);
        // Balance is a live TikTok read per account — best-effort, non-blocking.
        rows.filter((a) => a.isActive).forEach((a) => {
          tiktokService.getAccountBalance(a.accountUid)
            .then((b) => setBalances((prev) => ({ ...prev, [a.accountUid]: b })))
            .catch(() => setBalances((prev) => ({ ...prev, [a.accountUid]: null })));
        });
      })
      .catch((e) => setLoadError(errMsg(e))), []);

  const loadIdentities = useCallback(async () => {
    setIdentitiesLoading(true);
    try {
      const list = await tiktokService.listIdentities();
      // Custom identities were retired for ad creation — only real linked accounts count.
      setIdentities(list.filter((i) => i.identityType !== 'CUSTOMIZED_USER'));
    } catch {
      setIdentities([]);
    } finally { setIdentitiesLoading(false); }
  }, []);

  useEffect(() => {
    refresh();
    tiktokService.oauthStatus().then(setOauthStatus).catch(() => setOauthStatus({ productionConfigured: false }));
  }, [refresh]);

  useEffect(() => {
    if (accounts && accounts.length > 0) loadIdentities();
  }, [accounts, loadIdentities]);

  const showProd = oauthStatus?.productionConfigured ?? false;

  const connect = async () => {
    setBusy('connect');
    try {
      const r = await tiktokService.oauthStart();
      window.location.href = r.url;
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally { setBusy(null); }
  };

  const setDefault = async (acc: TiktokAdAccount) => {
    setBusy(acc.accountUid);
    try {
      await tiktokService.setDefault(acc.accountUid);
      toast.success(`${acc.advertiserName ?? acc.advertiserId} is now in use`);
      await refresh();
      await refreshTiktokAccountCurrency();
    } catch {
      // interceptor toasts
    } finally { setBusy(null); }
  };

  const refreshInfo = async (acc: TiktokAdAccount) => {
    setBusy(acc.accountUid);
    try {
      const r = await tiktokService.refreshInfo(acc.accountUid);
      await refresh();
      await refreshTiktokAccountCurrency();
      if (!r.verified) { toast.error(r.error || 'TikTok connection could not be verified.'); return; }
      toast.success(r.changed
        ? `Updated from TikTok — currency ${r.currency ?? '?'}, name ${r.advertiserName ?? '?'}`
        : `TikTok confirms current values (${r.currency ?? '?'}). Nothing to update.`);
    } catch {
      // interceptor toasts
    } finally { setBusy(null); }
  };

  const removeOne = async (acc: TiktokAdAccount) => {
    if (!window.confirm(`Remove ${acc.advertiserName ?? acc.advertiserId} from SlightorCRM? Campaigns stay in TikTok Ads Manager.`)) return;
    setBusy(acc.accountUid);
    try {
      await tiktokService.remove(acc.accountUid);
      toast.success('Advertiser removed');
      await refresh();
      await refreshTiktokAccountCurrency();
    } catch {
      // interceptor toasts
    } finally { setBusy(null); }
  };

  const disconnectAll = async () => {
    if (!accounts?.length) return;
    if (!window.confirm(`Disconnect TikTok? This removes ${accounts.length === 1 ? 'the advertiser' : `all ${accounts.length} advertisers`} from SlightorCRM. Campaigns stay in TikTok Ads Manager.`)) return;
    setBusy('disconnect');
    try {
      const results = await Promise.allSettled(accounts.map((a) => tiktokService.remove(a.accountUid)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed === 0) toast.success('TikTok disconnected');
      else toast.error(`${failed} advertiser${failed === 1 ? '' : 's'} could not be removed`);
      setIdentities(null);
      await refresh();
      await refreshTiktokAccountCurrency();
    } finally { setBusy(null); }
  };

  const connected = !!accounts && accounts.length > 0;
  const primary = accounts?.find((a) => a.isDefault) ?? accounts?.[0];
  const oldestGrant = accounts?.reduce<string | undefined>((min, a) => {
    const v = a.tokenGrantedAt ?? a.createdAt;
    return !min || (v && v < min) ? v : min;
  }, undefined);

  return (
    <TiktokShell
        title="Account"
        section="Account"
        description="Manage your advertiser, balance and linked TikTok accounts."
        actions={connected && showProd ? (
          <Button variant="secondary" size="sm" onClick={connect} loading={busy === 'connect'} iconLeft={<Music2 size={13} />}>
            Add advertiser
          </Button>
        ) : undefined}
      >

      <div className="w-full space-y-4">
        {loadError && <TiktokNotice title="Could not load account" error>{loadError} <Button size="sm" variant="ghost" onClick={refresh}>Retry</Button></TiktokNotice>}
        {zeroAdvertisersWarning && (
          <Card padded={false} className="flex items-start gap-3 border-warning/40 bg-warning-soft p-4 text-sm">
            <ShieldAlert size={18} className="mt-0.5 shrink-0 text-warning" />
            <div className="min-w-0 flex-1 space-y-2">
              <div>
                <p className="font-semibold text-text-primary">TikTok authorised — but returned no advertisers</p>
                <p className="mt-0.5 text-xs text-text-secondary">
                  OAuth succeeded, but the connected TikTok account has no advertisers SlightorCRM can manage. The usual causes, most frequent first:
                </p>
              </div>
              <ol className="list-decimal space-y-1.5 pl-5 text-xs text-text-secondary">
                <li>
                  <strong className="text-text-primary">Your TikTok app is in Sandbox / Development mode.</strong>{' '}
                  Only allow-listed advertisers can connect. Add yours in the{' '}
                  <a href="https://business-api.tiktok.com/portal/apps" target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-primary hover:underline">
                    Apps console <ExternalLink size={10} />
                  </a>{' '}
                  → your app → <strong>Sandbox</strong> → Add advertiser.
                </li>
                <li>
                  <strong className="text-text-primary">You signed in with a personal TikTok account.</strong>{' '}
                  TikTok For Business is a separate identity — sign in at{' '}
                  <a href="https://business.tiktok.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-primary hover:underline">
                    business.tiktok.com <ExternalLink size={10} />
                  </a>{' '}
                  first, then retry.
                </li>
                <li>
                  <strong className="text-text-primary">Your Business Center has no advertisers.</strong>{' '}
                  Create one in{' '}
                  <a href="https://ads.tiktok.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-primary hover:underline">
                    TikTok Ads Manager <ExternalLink size={10} />
                  </a>
                  , then connect again.
                </li>
                <li>
                  <strong className="text-text-primary">No advertiser was ticked on the consent screen.</strong>{' '}
                  Connect again and make sure every advertiser is checked before authorising.
                </li>
                <li>
                  <strong className="text-text-primary">Missing scopes.</strong>{' '}
                  Check the app’s approved permissions and advertiser access in the TikTok Business portal, then reconnect.
                </li>
              </ol>
              <div className="flex items-center gap-3 pt-1">
                {showProd && (
                  <Button size="sm" onClick={connect} loading={busy === 'connect'} iconLeft={<Music2 size={13} />}>Retry Connect TikTok</Button>
                )}
                <button type="button" onClick={() => setZeroAdvertisersWarning(false)} className="text-2xs text-text-muted hover:text-text-primary">
                  Dismiss
                </button>
              </div>
            </div>
          </Card>
        )}

        {accounts === null && !loadError && (
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
            <div className="space-y-4">
              <div className="skeleton h-28 rounded-2xl" />
              <div className="skeleton h-28 rounded-2xl" />
            </div>
            <div className="space-y-4">
              <div className="skeleton h-40 rounded-2xl" />
              <div className="skeleton h-48 rounded-2xl" />
            </div>
          </div>
        )}

        {accounts !== null && !connected && !loadError && (
          <ConnectCard onConnect={connect} busy={busy === 'connect'} canConnect={showProd} />
        )}

        {connected && (
          <div className="tt-account-layout">
            {/* Advertisers */}
            <div className="space-y-3">
              {accounts!.map((acc) => {
                const bal = balances[acc.accountUid];
                const inUse = acc.isDefault;
                return (
                  <Card key={acc.accountUid} padded={false}
                    className={clsx('p-5', inUse && 'ring-1 ring-primary/40', !acc.isActive && 'opacity-70')}>
                    <div className="flex flex-wrap items-start gap-4">
                      <BrandTile />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="truncate text-[15px] font-semibold leading-tight text-text-primary">
                            {acc.advertiserName || `Advertiser ${acc.advertiserId}`}
                          </span>
                          {inUse && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide text-on-primary">
                              <Check size={10} /> In use
                            </span>
                          )}
                          {acc.lastSyncError
                            ? <StatusPill status="error" label="Sync error" variant="danger" />
                            : <StatusPill status={acc.isActive ? 'active' : 'inactive'} label={acc.isActive ? 'Active' : 'Inactive'} variant={acc.isActive ? 'success' : 'neutral'} />}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-text-muted">
                          <StatusPill status={acc.healthError ? 'error' : acc.healthVerifiedAt ? 'verified' : 'unverified'}
                            variant={acc.healthError ? 'warning' : acc.healthVerifiedAt ? 'success' : 'neutral'}
                            label={acc.healthNeedsReconnect ? 'Reconnect needed' : acc.healthError ? 'Check failed' : acc.healthVerifiedAt ? 'Connection checked' : 'Not checked'} />
                          {acc.healthCheckedAt && <span>Checked {new Date(acc.healthCheckedAt).toLocaleString()}</span>}
                        </div>
                        {acc.healthError && <p className="mt-2 text-xs text-warning">{acc.healthError}</p>}
                        {acc.healthNeedsReconnect && showProd && <Button size="sm" variant="secondary" onClick={connect} loading={busy === 'connect'} className="mt-2">Reconnect TikTok</Button>}
                        <div className="mt-1 text-xs text-text-muted">
                          <span className="font-mono">{acc.advertiserId}</span>
                          {acc.currency ? ` · ${acc.currency}` : ''}
                          {acc.timezone ? ` · ${acc.timezone}` : ''}
                        </div>
                        {acc.lastSyncError && (
                          <div className="mt-1.5 truncate text-xs text-danger" title={acc.lastSyncError}>{acc.lastSyncError}</div>
                        )}
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {!inUse && (
                            <Button variant="secondary" size="sm" onClick={() => setDefault(acc)} loading={busy === acc.accountUid} disabled={!!busy}>
                              Use this advertiser
                            </Button>
                          )}
                          <IconButton icon={<RefreshCw size={14} />} title="Refresh name, currency and timezone from TikTok"
                            onClick={() => refreshInfo(acc)} disabled={!!busy} />
                          <IconButton icon={<Trash2 size={14} />} title="Remove this advertiser" danger
                            onClick={() => removeOne(acc)} disabled={!!busy} />
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">Spendable balance</div>
                        <div className="mt-1 font-display text-2xl font-semibold tabular-nums tracking-tight text-text-primary">
                          {bal === undefined && acc.isActive
                            ? <span className="inline-flex h-8 items-center"><Spinner size={16} className="text-text-muted" /></span>
                            : fmtMoney(bal?.accountBalance, bal?.currency ?? acc.currency)}
                        </div>
                        <div className="mt-1 text-xs text-text-muted">
                          {bal
                            ? <>
                                {bal.bcCashBalance != null && <span>Business Center {fmtMoney(bal.bcCashBalance, bal.currency)}</span>}
                                {(bal.grantBalance ?? 0) > 0 && <span>{bal.bcCashBalance != null ? ' · ' : ''}Ad credit {fmtMoney(bal.grantBalance, bal.currency)}</span>}
                                {bal.bcCashBalance == null && !(bal.grantBalance ?? 0) && <span>Advertiser level</span>}
                              </>
                            : bal === null ? 'Balance not reported' : !acc.isActive ? 'Inactive advertiser' : 'Reading from TikTok…'}
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Linked accounts and connection details */}
            <div className="tt-account-details">
              <Card>
                <SectionHeader
                  title="TikTok identities"
                  subtitle="Accounts your ads can run under"
                  icon={<UserCircle2 size={16} />}
                  action={
                    <button type="button" onClick={loadIdentities} disabled={identitiesLoading} title="Re-check" aria-label="Refresh identities"
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary disabled:opacity-50">
                      {identitiesLoading ? <Spinner size={13} /> : <RefreshCw size={14} />}
                    </button>
                  }
                />
                {identities === null ? (
                  <div className="space-y-2">
                    <div className="skeleton h-11 rounded-xl" />
                    <div className="skeleton h-11 rounded-xl" />
                  </div>
                ) : identities.length === 0 ? (
                  <div className="rounded-xl border border-warning/30 bg-warning-soft p-3.5 text-xs leading-relaxed text-text-secondary">
                    <p className="font-semibold text-text-primary">No TikTok account is linked to this advertiser.</p>
                    <p className="mt-1">
                      Ads shown in the TikTok feed need a real TikTok account. Link one in TikTok
                      Ads Manager (Assets → Identities or Business Center → Users → TikTok accounts), then refresh here.
                    </p>
                    <a href="https://ads.tiktok.com" target="_blank" rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-1 font-medium text-primary hover:underline">
                      Open TikTok Ads Manager <ExternalLink size={11} />
                    </a>
                  </div>
                ) : (
                  <ul className="divide-y divide-border/60">
                    {identities.map((i) => (
                      <li key={i.identityId} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                        {i.profileImage
                          ? <img src={i.profileImage} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" referrerPolicy="no-referrer" />
                          : <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-alt text-text-muted"><UserCircle2 size={18} /></span>}
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-text-primary">{i.displayName || i.identityId}</div>
                          <div className="truncate font-mono text-2xs text-text-muted">{i.identityId}</div>
                        </div>
                        <StatusPill status={i.identityType} variant={i.identityType === 'BC_AUTH_TT' ? 'info' : 'success'}
                          label={i.identityType === 'BC_AUTH_TT' ? 'Business Center' : i.identityType === 'TT_USER' ? 'Linked' : i.identityType} />
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <Card>
                <SectionHeader title="Connection" subtitle="Authorisation and recent activity" icon={<KeyRound size={16} />} />
                <div className="divide-y divide-border/60">
                  <Row label="Business account">{primary?.advertiserName || primary?.advertiserId || '—'}</Row>
                  {primary?.role && <Row label="Role">{primary.role.replace(/^ROLE_/, '').replace(/_/g, ' ').toLowerCase()}</Row>}
                  <Row label="Connected">{fmtDate(oldestGrant)}</Row>
                  <Row label="Last sync">{primary?.lastSyncedAt ? new Date(primary.lastSyncedAt).toLocaleString() : '—'}</Row>
                </div>
                <div className="mt-4 flex items-center justify-between gap-3 border-t border-border/60 pt-4">
                  <a href="https://business-api.tiktok.com/portal" target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-primary">
                    TikTok Business portal <ExternalLink size={11} />
                  </a>
                  <Button variant="secondary" size="sm" onClick={disconnectAll} loading={busy === 'disconnect'} disabled={!!busy && busy !== 'disconnect'}
                    className="hover:border-danger hover:text-danger" iconLeft={<Unplug size={13} />}>
                    Disconnect
                  </Button>
                </div>
              </Card>

              {!showProd && (
                <Card padded={false} className="flex items-start gap-2.5 border-warning/30 bg-warning-soft p-4 text-xs text-text-secondary">
                  <ShieldAlert size={15} className="mt-0.5 shrink-0 text-warning" />
                  <span>TikTok OAuth isn't configured on this server, so new advertisers can't be added until the app credentials are set.</span>
                </Card>
              )}
            </div>
          </div>
        )}

        {connected && (
          <div className="flex items-center gap-2 text-xs text-text-muted">
            <Wallet size={12} />
            Balances are read live from TikTok for active advertisers. Money usually sits at Business Center level; the advertiser figure alone can read 0.
            <button type="button" onClick={() => navigate('/tiktok/campaigns')} className="ml-auto font-medium text-primary hover:underline">
              Go to campaigns
            </button>
          </div>
        )}
      </div>
    </TiktokShell>
  );
}
